/**
 * Owned Media (OML) — contratos compartidos back/front.
 *
 * Fuente: "Owned Media — Apartado Owned Media — Requerimientos" (doc de
 * requerimientos, 18/08/2026). Todo lo que acá es una constante sale de una
 * tabla del doc: las entidades (§5.1), sus campos (§5.2), las listas cerradas
 * (§5.3), los campos restringidos (§9.3) y las máquinas de estado (§13.2).
 *
 * El schema de campos es DATA (no código): el front lo usa para dibujar los
 * formularios y el back para validar tipos, requeridos y listas. Las reglas
 * que cruzan campos o entidades ("no se puede X sin Y", §13.3) viven en
 * `shared/oml-rules.ts`, puras y testeadas.
 */

// ─── Entidades ───────────────────────────────────────────────────────────────

export const OML_ENTITY_TYPES = [
  'account',
  'brand',
  'stakeholder',
  'platform',
  'journey',
  'campaign',
  'experiment',
  'project',
  'process',
  'channelRule',
  'segment',
  'tag',
  'assignment',
  'link',
  'report',
  'commChannel',
] as const;
export type OmlEntityType = (typeof OML_ENTITY_TYPES)[number];

/** Entidades hijas (todas menos la cuenta). */
export const OML_CHILD_TYPES = OML_ENTITY_TYPES.filter((t) => t !== 'account') as Exclude<OmlEntityType, 'account'>[];
export type OmlChildType = (typeof OML_CHILD_TYPES)[number];

/** Prefijo del sort key en Dynamo (`<PREFIX>#<id>`). */
export const OML_SK_PREFIX: Record<OmlEntityType, string> = {
  account: 'META',
  brand: 'BRAND',
  stakeholder: 'STAKEHOLDER',
  platform: 'PLATFORM',
  journey: 'JOURNEY',
  campaign: 'CAMPAIGN',
  experiment: 'EXPERIMENT',
  project: 'PROJECT',
  process: 'PROCESS',
  channelRule: 'CHANNEL_RULE',
  segment: 'SEGMENT',
  tag: 'TAG',
  assignment: 'ASSIGNMENT',
  link: 'LINK',
  report: 'REPORT',
  commChannel: 'COMM_CHANNEL',
};

// ─── Listas cerradas (§5.3) ──────────────────────────────────────────────────

export const OML_LIST_IDS = [
  'client',
  'country',
  'serviceType',
  'accountStatus',
  'platform',
  'channel',
  'commChannelType',
  'usageIntensity',
  'stakeholderSeniority',
  'decisionLevel',
  'stakeholderType',
  'organizationType',
  'approvalScope',
  'journeyStatus',
  'measurementStatus',
  'experimentStatus',
  'experimentDecision',
  'noUpliftDiagnosis',
  'controlGroupLevel',
  'implementationPath',
  'flowComplexity',
  'projectStatus',
  'deliverableType',
  'processType',
  'accessType',
  'urlParamsToStrip',
  'omRole',
  'omTeam',
  'personType',
  'campaignType',
  'segmentType',
  'linkType',
  'resourceOwner',
  'frequency',
  'reportFormat',
  'metricsProvider',
] as const;
export type OmlListId = (typeof OML_LIST_IDS)[number];

export interface OmlListDef {
  id: OmlListId;
  /** Etiqueta en español (idioma del contenido). La UI la traduce por clave `oml.lists.<id>`. */
  label: string;
  values: string[];
  /** `locked`: cambiarla cambia el flujo o rompe la comparabilidad ⇒ NO se edita desde el panel (§5.3). */
  locked: boolean;
  /** Quién agrega valores nuevos (informativo). */
  editor: 'lead' | 'admin';
}

export const OML_LISTS: Record<OmlListId, OmlListDef> = {
  client: {
    id: 'client', label: 'Cliente', locked: false, editor: 'lead',
    values: ['Shell', 'Despegar', 'DolFinTech', 'Burger King', 'Laika', 'JCPM', 'Telecom', 'Meta', 'Primax', 'Natura', 'Scholas'],
  },
  country: {
    id: 'country', label: 'País o región', locked: false, editor: 'lead',
    values: ['Argentina', 'Brasil', 'Chile', 'Colombia', 'Estados Unidos', 'México', 'Perú', 'Uruguay', 'LATAM', 'Global'],
  },
  serviceType: {
    id: 'serviceType', label: 'Tipo de servicio', locked: false, editor: 'lead',
    values: ['Operación diaria', 'Migración de Owned Media', 'Estrategia de Owned Media', 'Estrategia de journeys', 'Ejecución', 'Full Owned Media', 'Producción de contenido', 'Diseño y HTML', 'Prueba de concepto (POC)', 'Consultoría'],
  },
  accountStatus: {
    id: 'accountStatus', label: 'Estado de cuenta', locked: true, editor: 'lead',
    values: ['En onboarding', 'Activa', 'Pausada', 'Cerrada', 'Archivada'],
  },
  platform: {
    id: 'platform', label: 'Plataforma', locked: false, editor: 'admin',
    values: ['Braze', 'CleverTap', 'Insider', 'Insider One', 'Fidelizador', 'Firebase', 'Responsys', 'Salesforce Marketing Cloud', 'Go High Level', 'EmBlue', 'Knak', 'Stripo', 'Hablame', 'Wise', 'Journey Builder', 'Be Free', 'Amplitude', 'Mixpanel', 'Google Analytics', 'Google Tag Manager', 'XML Product Feed', 'ClickUp', 'Trello', 'Notion', 'Miro', 'Figma', 'Google Sheets', 'Google Drive', 'Google Colab', 'Dropbox', 'SharePoint', 'Power BI', 'Databricks', 'AWS', 'WordPress', 'WPForms', 'Stripe', 'PayPal'],
  },
  channel: {
    id: 'channel', label: 'Canal', locked: false, editor: 'lead',
    values: ['Email', 'Push', 'Web push', 'In-app', 'SMS', 'WhatsApp', 'Banner', 'Landing page', 'Story', 'Countdown', 'Survey', 'Recomendación de producto', 'Carrusel de producto'],
  },
  commChannelType: {
    id: 'commChannelType', label: 'Tipo de canal de comunicación con el cliente', locked: false, editor: 'lead',
    values: ['Slack', 'WhatsApp', 'Teams', 'Google Chat', 'Google Meet', 'Email', 'Llamada'],
  },
  usageIntensity: {
    id: 'usageIntensity', label: 'Intensidad de uso', locked: true, editor: 'lead',
    values: ['Alto', 'Medio', 'Poco', 'No se usa'],
  },
  stakeholderSeniority: {
    id: 'stakeholderSeniority', label: 'Seniority de stakeholder', locked: true, editor: 'lead',
    values: ['Top 1', 'Top 2', 'Top 3', 'Top 4'],
  },
  decisionLevel: {
    id: 'decisionLevel', label: 'Nivel de decisión', locked: true, editor: 'lead',
    values: ['Alto', 'Medio', 'Bajo'],
  },
  stakeholderType: {
    id: 'stakeholderType', label: 'Tipo de stakeholder', locked: true, editor: 'lead',
    values: ['Cliente', 'Tercero', 'Winclap'],
  },
  organizationType: {
    id: 'organizationType', label: 'Tipo de organización', locked: false, editor: 'lead',
    values: ['Cliente', 'Agencia de desarrollo', 'Agencia creativa', 'Plataforma (KAM o soporte)', 'Banco o partner comercial', 'Proveedor', 'Winclap'],
  },
  approvalScope: {
    id: 'approvalScope', label: 'Aprueba', locked: true, editor: 'lead',
    values: ['Contenido', 'Diseño', 'Lanzamiento', 'Datos y listas', 'Nada'],
  },
  journeyStatus: {
    id: 'journeyStatus', label: 'Estado de journey', locked: true, editor: 'lead',
    values: ['Propuesto', 'En diseño', 'En implementación', 'Activo', 'Pausado', 'Descartado', 'Archivado'],
  },
  measurementStatus: {
    id: 'measurementStatus', label: 'Estado de medición', locked: true, editor: 'lead',
    values: ['Medido', 'Sin medición', 'Medición rota'],
  },
  experimentStatus: {
    id: 'experimentStatus', label: 'Estado de experimento', locked: true, editor: 'lead',
    values: ['Diseñado', 'Corriendo', 'Cerrado', 'Descartado'],
  },
  experimentDecision: {
    id: 'experimentDecision', label: 'Decisión de experimento', locked: false, editor: 'lead',
    values: ['Sostener', 'Iterar', 'Descartar', 'Extender el período o sumar audiencia', 'Volver a correr el experimento'],
  },
  noUpliftDiagnosis: {
    id: 'noUpliftDiagnosis', label: 'Diagnóstico sin uplift', locked: true, editor: 'lead',
    values: ['Experimento bien hecho, sin efecto real', 'Muestra insuficiente', 'Sospecha de contaminación del control', 'Sin efecto en el KPI principal pero con señal en otras métricas'],
  },
  controlGroupLevel: {
    id: 'controlGroupLevel', label: 'Nivel de grupo control', locked: true, editor: 'lead',
    values: ['Campaña o flujo puntual', 'Canal', 'Audiencia o segmento', 'Global (todo Owned Media)'],
  },
  implementationPath: {
    id: 'implementationPath', label: 'Camino de implementación', locked: true, editor: 'lead',
    values: ['Pausar el flujo viejo y activar el nuevo', 'Correr las dos versiones en simultáneo'],
  },
  flowComplexity: {
    id: 'flowComplexity', label: 'Clasificación del flujo', locked: true, editor: 'lead',
    values: ['Simple', 'Complejo'],
  },
  projectStatus: {
    id: 'projectStatus', label: 'Estado de proyecto', locked: true, editor: 'lead',
    values: ['Bajada recibida', 'Esperando assets', 'En producción', 'En revisión interna', 'En validación del cliente', 'Aprobado', 'Entregado', 'Cancelado'],
  },
  deliverableType: {
    id: 'deliverableType', label: 'Tipo de entregable', locked: false, editor: 'lead',
    values: ['Email', 'Adaptación de email', 'Landing page', 'In-app', 'Push', 'Banner', 'Copy', 'HTML', 'Pieza visual', 'Template', 'Newsletter'],
  },
  processType: {
    id: 'processType', label: 'Tipo de proceso operativo', locked: false, editor: 'lead',
    values: ['Planning mensual', 'Planning semanal', 'Ejecución y seguimiento', 'Rutina diaria', 'Reporting', 'QA operativo', 'Riesgos conocidos en QA', 'Naming de campañas', 'Matriz de SLA', 'Estructura de mensajes con el cliente', 'Horarios de atención', 'Gobernanza de la información', 'Cadena de aprobación', 'Responsabilidades del cliente', 'Fuera de alcance del servicio', 'Otros puntos importantes'],
  },
  accessType: {
    id: 'accessType', label: 'Tipo de acceso', locked: true, editor: 'admin',
    values: ['SSO Winclap', 'Cuenta nominal Winclap', 'Cuenta nominal del cliente', 'Cuenta compartida del cliente', 'Cuenta personal'],
  },
  urlParamsToStrip: {
    id: 'urlParamsToStrip', label: 'Parámetros de URL a eliminar', locked: false, editor: 'admin',
    values: ['token', 'access_token', 'auth', 'key', 'rlkey', 'e', 'state', 'tid', 'cid', 'email', 'password', 'sig', 'signature'],
  },
  omRole: {
    id: 'omRole', label: 'Rol OM', locked: false, editor: 'lead',
    values: ['Growth Partner', 'Lead OMC', 'Retention Lead', 'Content Lead', 'Content Partner', 'Retention Analyst', 'Retention Sr. Analyst', 'Account', 'Diseñador freelance', 'Maquetador freelance'],
  },
  omTeam: {
    id: 'omTeam', label: 'Equipo', locked: true, editor: 'lead',
    values: ['Content (OMC)', 'Retention', 'Growth', 'Diseño'],
  },
  personType: {
    id: 'personType', label: 'Tipo de persona', locked: true, editor: 'lead',
    values: ['Winclap', 'Freelance externo'],
  },
  campaignType: {
    id: 'campaignType', label: 'Tipo de campaña', locked: false, editor: 'lead',
    values: ['Promocional', 'Promoción bancaria', 'Contenido de valor', 'Trade', 'Retail', 'Newsletter', 'Administrativa', 'Informativa', 'Transaccional'],
  },
  segmentType: {
    id: 'segmentType', label: 'Tipo de segmento', locked: false, editor: 'lead',
    values: ['Lifecycle', 'Geográfico', 'Membresía', 'Comportamiento', 'Transaccional', 'Nativo de plataforma'],
  },
  linkType: {
    id: 'linkType', label: 'Tipo de link', locked: false, editor: 'lead',
    values: ['Calendario', 'Tracker', 'Brandbook', 'Assets', 'Figma', 'Reporte', 'Propuesta', 'Dashboard', 'Manual operativo', 'Taxonomía de eventos', 'Otro'],
  },
  resourceOwner: {
    id: 'resourceOwner', label: 'Dueño del recurso', locked: true, editor: 'lead',
    values: ['Winclap', 'Cliente', 'Tercero'],
  },
  frequency: {
    id: 'frequency', label: 'Frecuencia', locked: false, editor: 'lead',
    values: ['Diaria', 'Semanal', 'Quincenal', 'Cada 10 días', 'Mensual', 'Trimestral', 'A pedido del cliente', 'Proyecto a proyecto'],
  },
  reportFormat: {
    id: 'reportFormat', label: 'Formato de reporte', locked: false, editor: 'lead',
    values: ['Google Slides', 'PPTX', 'Google Sheets', 'Looker', 'PDF', 'Documento', 'Demostración en vivo'],
  },
  metricsProvider: {
    id: 'metricsProvider', label: 'Quién provee las métricas', locked: true, editor: 'lead',
    values: ['Winclap', 'El cliente', 'Mixto'],
  },
};

/** Valores por defecto de todas las listas (lo que carga el panel la primera vez). */
export const OML_DEFAULT_CATALOGS: Record<OmlListId, string[]> = Object.fromEntries(
  OML_LIST_IDS.map((id) => [id, [...OML_LISTS[id].values]]),
) as Record<OmlListId, string[]>;

// Literales de estado que usan las reglas (sin strings sueltos en el código).
export const ACCOUNT_STATUS = {
  ONBOARDING: 'En onboarding', ACTIVE: 'Activa', PAUSED: 'Pausada', CLOSED: 'Cerrada', ARCHIVED: 'Archivada',
} as const;
export const JOURNEY_STATUS = {
  PROPOSED: 'Propuesto', DESIGN: 'En diseño', IMPLEMENTATION: 'En implementación', ACTIVE: 'Activo', PAUSED: 'Pausado', DISCARDED: 'Descartado', ARCHIVED: 'Archivado',
} as const;
export const MEASUREMENT_STATUS = { MEASURED: 'Medido', NONE: 'Sin medición', BROKEN: 'Medición rota' } as const;
export const EXPERIMENT_STATUS = { DESIGNED: 'Diseñado', RUNNING: 'Corriendo', CLOSED: 'Cerrado', DISCARDED: 'Descartado' } as const;
export const PROJECT_STATUS = {
  BRIEF: 'Bajada recibida', AWAITING_ASSETS: 'Esperando assets', PRODUCTION: 'En producción', INTERNAL_REVIEW: 'En revisión interna',
  CLIENT_VALIDATION: 'En validación del cliente', APPROVED: 'Aprobado', DELIVERED: 'Entregado', CANCELLED: 'Cancelado',
} as const;
export const ACCESS_TYPE = {
  SSO: 'SSO Winclap', NOMINAL_WINCLAP: 'Cuenta nominal Winclap', NOMINAL_CLIENT: 'Cuenta nominal del cliente',
  SHARED_CLIENT: 'Cuenta compartida del cliente', PERSONAL: 'Cuenta personal',
} as const;
/** Tipos de acceso que disparan alerta permanente + notificación (§5.2 Plataforma y acceso). */
export const RISKY_ACCESS_TYPES: readonly string[] = [ACCESS_TYPE.SHARED_CLIENT, ACCESS_TYPE.PERSONAL];
export const STAKEHOLDER_TYPE = { CLIENT: 'Cliente', THIRD_PARTY: 'Tercero', WINCLAP: 'Winclap' } as const;
export const PERSON_TYPE = { WINCLAP: 'Winclap', FREELANCE: 'Freelance externo' } as const;
export const OM_ROLE = {
  GROWTH_PARTNER: 'Growth Partner', LEAD_OMC: 'Lead OMC', RETENTION_LEAD: 'Retention Lead', CONTENT_LEAD: 'Content Lead',
  CONTENT_PARTNER: 'Content Partner', RETENTION_ANALYST: 'Retention Analyst', RETENTION_SR_ANALYST: 'Retention Sr. Analyst',
  ACCOUNT: 'Account', DESIGNER_FREELANCE: 'Diseñador freelance', LAYOUT_FREELANCE: 'Maquetador freelance',
} as const;
/** Roles de asignación que hacen "Content Partner" y "Retention Analyst" a efectos de permisos (§9.1). */
export const CONTENT_PARTNER_ROLES: readonly string[] = [OM_ROLE.CONTENT_PARTNER, OM_ROLE.CONTENT_LEAD];
export const RETENTION_ANALYST_ROLES: readonly string[] = [OM_ROLE.RETENTION_ANALYST, OM_ROLE.RETENTION_SR_ANALYST];
/** Roles de asignación de diseño/maquetación (Proyecto → Freelo asignado). */
export const DESIGN_ROLES: readonly string[] = [OM_ROLE.DESIGNER_FREELANCE, OM_ROLE.LAYOUT_FREELANCE];
export const LINK_TYPE_CALENDAR = 'Calendario';
export const PROCESS_TYPE_NAMING = 'Naming de campañas';
/** Bloques de proceso que solo edita el Lead OMC (§8.3). */
export const LEAD_ONLY_PROCESS_TYPES: readonly string[] = ['Matriz de SLA', 'Fuera de alcance del servicio', 'Responsabilidades del cliente'];

// ─── Schema de campos (§5.2) ─────────────────────────────────────────────────

export type OmlFieldType =
  | 'text'
  | 'richtext'
  | 'number'
  | 'percent'
  | 'date'
  | 'dateMonth' // fecha con precisión variable: 'YYYY-MM' o 'YYYY-MM-DD'
  | 'bool'
  | 'select'
  | 'multiselect'
  | 'user' // email de Winclap
  | 'link'
  | 'file' // un asset
  | 'files' // varios assets
  | 'relation' // id de una entidad hija de la misma cuenta
  | 'relations';

export type OmlRestrictedGroup = 'commercial' | 'credential';

export interface OmlFieldDef {
  key: string;
  /** Etiqueta en español (defaultValue de `oml.fields.<entity>.<key>`). */
  label: string;
  type: OmlFieldType;
  required?: boolean;
  /** Requerido cuando otro campo vale X (las reglas más finas están en oml-rules). */
  requiredIf?: { field: string; equals: unknown };
  /** Requerido cuando otro campo tiene valor (Deal actual ⇒ Fecha de vigencia). */
  requiredIfPresent?: string;
  list?: OmlListId;
  relation?: OmlChildType;
  restricted?: OmlRestrictedGroup;
  /** Lo escribe el sistema: no se edita ni se manda desde el formulario. */
  system?: boolean;
  default?: unknown;
  min?: number;
  max?: number;
  integer?: boolean;
  /** No bloquea el guardado pero sin él la ficha no llega al 100 % y aparece en Salud (§5.2 KPIs, capacity, deal). */
  recommended?: boolean;
  /** Único dentro de la cuenta (o de cuenta + marca si `uniqueWith`). */
  unique?: boolean;
  uniqueWith?: string;
  /** Regla / ayuda en español (defaultValue de `oml.help.<entity>.<key>`). */
  help?: string;
  /** Quién lo carga (informativo, §5.2). */
  loadedBy?: string;
  /** Cambiarlo exige justificación escrita (§8.5). */
  justify?: boolean;
  /** Este campo es "el estado" de la entidad (máquina de estados). */
  isStatus?: boolean;
  /** Este campo indica baja lógica (activo/inactivo) de la entidad. */
  isActiveFlag?: boolean;
}

export interface OmlEntityDef {
  type: OmlEntityType;
  label: string;
  labelPlural: string;
  fields: OmlFieldDef[];
  /** Campo que nombra al registro en listados y en el historial. */
  nameField: string;
  /** Acciones habilitadas (§8.3). */
  actions: { duplicate: boolean; archive: boolean; delete: boolean; upload: boolean };
}

const F = (f: OmlFieldDef): OmlFieldDef => f;

export const OML_SCHEMA: Record<OmlEntityType, OmlEntityDef> = {
  account: {
    type: 'account', label: 'Cuenta OM', labelPlural: 'Cuentas OM', nameField: 'name',
    actions: { duplicate: false, archive: true, delete: false, upload: true },
    fields: [
      F({ key: 'name', label: 'Nombre de la cuenta', type: 'text', required: true, unique: true, loadedBy: 'Lead OMC', help: 'Único. Convención sugerida: Cliente + mercado (Shell Chile, Despegar LATAM, Meta Global; Burger King Argentina / Burger King Chile; Despegar B2C / Despegar B2B).' }),
      F({ key: 'client', label: 'Cliente', type: 'select', list: 'client', required: true, loadedBy: 'Lead OMC' }),
      F({ key: 'countries', label: 'País o región', type: 'multiselect', list: 'country', required: true, loadedBy: 'Lead OMC', help: 'Al menos 1. Admite LATAM y Global para cuentas que no operan por país.' }),
      F({ key: 'serviceTypes', label: 'Tipo de servicio', type: 'multiselect', list: 'serviceType', required: true, loadedBy: 'Lead OMC', help: 'Al menos 1. Múltiple porque hay cuentas con servicios combinados.' }),
      F({ key: 'startDate', label: 'Fecha de inicio', type: 'dateMonth', required: true, loadedBy: 'Lead OMC', help: 'Admite precisión de mes o de día.' }),
      F({ key: 'endDate', label: 'Fecha de fin prevista', type: 'dateMonth', loadedBy: 'Lead OMC', help: 'Si se completa, debe ser posterior al inicio. Vacío = sin fecha de fin definida.' }),
      F({ key: 'previousPeriods', label: 'Períodos de contrato anteriores', type: 'richtext', help: 'Para cuentas con contratación discontinua.' }),
      F({ key: 'status', label: 'Estado', type: 'select', list: 'accountStatus', required: true, default: 'En onboarding', isStatus: true, loadedBy: 'Lead OMC' }),
      F({ key: 'growthPartners', label: 'Growth Partner', type: 'relations', relation: 'assignment', system: true, help: 'Derivado de las Asignaciones de equipo vigentes con rol Growth Partner. Puede haber más de uno.' }),
      F({ key: 'functionalOwner', label: 'Owner funcional de la ficha', type: 'user', required: true, loadedBy: 'Lead OMC', help: 'Responde por que el contenido esté al día. La cuenta no se puede guardar sin él.' }),
      F({ key: 'clientSummary', label: 'Resumen del cliente', type: 'richtext', required: true, loadedBy: 'Growth Partner', help: 'Qué hace el cliente y cuál es su negocio. La operación va en Proceso operativo.' }),
      F({ key: 'currentStatus', label: 'Estado actual y pendientes', type: 'richtext', required: true, loadedBy: 'Lead OMC', help: 'Qué está prendido, qué falta y qué está bloqueado hoy. Se revisa al menos una vez al mes.' }),
      F({ key: 'mainObjective', label: 'Objetivo principal del cliente', type: 'richtext', required: true, loadedBy: 'Growth Partner' }),
      F({ key: 'kpis', label: 'KPIs principales', type: 'richtext', recommended: true, loadedBy: 'Retention Lead', help: 'Si está vacío, la ficha no llega al 100 % y aparece en la vista de salud.' }),
      F({ key: 'attributionWindow', label: 'Ventana de atribución', type: 'text', recommended: true, loadedBy: 'Retention Analyst', help: 'Sin esto, ningún CVR de la cuenta es interpretable.' }),
      F({ key: 'metricsProvider', label: 'Quién provee las métricas', type: 'select', list: 'metricsProvider', required: true, default: 'Winclap', loadedBy: 'Retention Analyst' }),
      F({ key: 'deal', label: 'Deal actual', type: 'richtext', recommended: true, restricted: 'commercial', justify: true, loadedBy: 'Growth Partner', help: 'Campo restringido. Exige Fecha de vigencia del deal. Lo carga el Growth Partner: no bloquea el alta de la cuenta, pero sin él la ficha no llega al 100 %.' }),
      F({ key: 'dealValidUntil', label: 'Fecha de vigencia del deal', type: 'date', recommended: true, requiredIfPresent: 'deal', restricted: 'commercial', loadedBy: 'Growth Partner', help: 'Campo restringido. Requerido si Deal actual está completo.' }),
      F({ key: 'contractCaps', label: 'Topes contratados', type: 'richtext', restricted: 'commercial', justify: true, loadedBy: 'Growth Partner', help: 'Campo restringido. Los límites cuantificados del contrato, uno por línea.' }),
      F({ key: 'sowLink', label: 'Link al SOW', type: 'link', restricted: 'commercial', loadedBy: 'Growth Partner', help: 'Campo restringido.' }),
      F({ key: 'capacitySummary', label: 'Capacity asignado (resumen)', type: 'text', recommended: true, restricted: 'commercial', justify: true, loadedBy: 'Lead OMC', help: 'Campo restringido. Resumen legible; el dato sumable vive en Asignación de equipo → Dedicación.' }),
      F({ key: 'activeChannels', label: 'Canales de campaña activos', type: 'multiselect', list: 'channel', loadedBy: 'Retention Analyst', help: 'No es requerido: hay cuentas de producción de contenido que no ejecutan canales.' }),
      F({ key: 'logoAssetId', label: 'Logo de la cuenta', type: 'file', loadedBy: 'Content Partner', help: 'PNG, JPG o SVG. Máximo 5 MB.' }),
      F({ key: 'retentionDeadline', label: 'Eliminar el contenido el', type: 'date', loadedBy: 'Admin del apartado', help: 'Solo si un contrato exige un plazo de retención menor a 24 meses tras el cierre (§10.2).' }),
      F({ key: 'lastReviewedAt', label: 'Última revisión de la ficha', type: 'date', system: true, help: 'Lo escribe el sistema en cada guardado. A los 30 días sin edición la ficha se marca como desactualizada.' }),
      F({ key: 'currentStatusUpdatedAt', label: 'Última actualización de "Estado actual y pendientes"', type: 'date', system: true }),
    ],
  },
  brand: {
    type: 'brand', label: 'Marca / Workspace', labelPlural: 'Marcas y workspaces', nameField: 'name',
    actions: { duplicate: false, archive: true, delete: false, upload: false },
    fields: [
      F({ key: 'name', label: 'Nombre de la marca o unidad', type: 'text', required: true, unique: true, loadedBy: 'Content Partner' }),
      F({ key: 'market', label: 'Ciudad o mercado', type: 'text', loadedBy: 'Content Partner' }),
      F({ key: 'platform', label: 'Plataforma', type: 'select', list: 'platform', loadedBy: 'Retention Analyst' }),
      F({ key: 'workspaceId', label: 'Identificador del workspace', type: 'text', loadedBy: 'Retention Analyst', help: 'El subdominio o identificador del workspace en la plataforma.' }),
      F({ key: 'ownVisualIdentity', label: 'Tiene identidad visual propia', type: 'bool', required: true, default: false, loadedBy: 'Content Partner' }),
      F({ key: 'active', label: 'Activa', type: 'bool', required: true, default: true, isActiveFlag: true, loadedBy: 'Content Partner' }),
    ],
  },
  stakeholder: {
    type: 'stakeholder', label: 'Stakeholder', labelPlural: 'Stakeholders', nameField: 'fullName',
    actions: { duplicate: false, archive: false, delete: false, upload: false },
    fields: [
      F({ key: 'fullName', label: 'Nombre y apellido', type: 'text', required: true, loadedBy: 'Content Partner' }),
      F({ key: 'stakeholderType', label: 'Tipo de stakeholder', type: 'select', list: 'stakeholderType', required: true, default: 'Cliente', loadedBy: 'Content Partner' }),
      F({ key: 'organizationType', label: 'Tipo de organización', type: 'select', list: 'organizationType', requiredIf: { field: 'stakeholderType', equals: 'Tercero' }, loadedBy: 'Content Partner', help: 'Requerido si el tipo es Tercero.' }),
      F({ key: 'organization', label: 'Organización', type: 'text', requiredIf: { field: 'stakeholderType', equals: 'Tercero' }, loadedBy: 'Content Partner', help: 'Requerido si el tipo es Tercero.' }),
      F({ key: 'role', label: 'Rol', type: 'text', required: true, loadedBy: 'Content Partner', help: 'El rol de la persona en su propia organización.' }),
      F({ key: 'seniority', label: 'Seniority', type: 'select', list: 'stakeholderSeniority', loadedBy: 'Growth Partner', help: 'Escala única para todo el apartado.' }),
      F({ key: 'decisionLevel', label: 'Nivel de decisión', type: 'select', list: 'decisionLevel', loadedBy: 'Growth Partner' }),
      F({ key: 'email', label: 'Email', type: 'text', loadedBy: 'Content Partner', help: 'Visible para el equipo de la cuenta: es un dato de trabajo.' }),
      F({ key: 'phone', label: 'Teléfono', type: 'text', restricted: 'commercial', loadedBy: 'Content Partner', help: 'Campo restringido.' }),
      F({ key: 'approves', label: 'Aprueba', type: 'multiselect', list: 'approvalScope', loadedBy: 'Content Partner', help: 'Permite reconstruir la cadena de aprobación de la cuenta.' }),
      F({ key: 'notes', label: 'Notas', type: 'richtext', loadedBy: 'Content Partner', help: 'No usar para juicios sobre la persona.' }),
      F({ key: 'active', label: 'Activo', type: 'bool', required: true, default: true, isActiveFlag: true, loadedBy: 'Content Partner', help: 'Al marcar No deja de aparecer en los listados pero se conserva el histórico.' }),
    ],
  },
  platform: {
    type: 'platform', label: 'Plataforma y acceso', labelPlural: 'Plataformas y accesos', nameField: 'platform',
    actions: { duplicate: false, archive: true, delete: false, upload: false },
    fields: [
      F({ key: 'brandId', label: 'Marca / Workspace', type: 'relation', relation: 'brand', loadedBy: 'Retention Analyst', help: 'Se completa cuando una misma plataforma tiene un workspace por marca.' }),
      F({ key: 'platform', label: 'Plataforma', type: 'select', list: 'platform', required: true, loadedBy: 'Retention Analyst' }),
      F({ key: 'usage', label: 'Uso', type: 'text', required: true, loadedBy: 'Retention Analyst' }),
      F({ key: 'isCentral', label: 'Es plataforma central', type: 'bool', required: true, default: false, loadedBy: 'Retention Analyst', help: 'Como máximo una plataforma por cuenta y marca puede ser central.' }),
      F({ key: 'accessType', label: 'Tipo de acceso', type: 'select', list: 'accessType', required: true, justify: true, loadedBy: 'Retention Analyst', help: '"Cuenta compartida del cliente" y "Cuenta personal" muestran alerta permanente y notifican al data owner y al Lead OMC.' }),
      F({ key: 'credentialLocation', label: 'Dónde está la credencial', type: 'text', required: true, restricted: 'credential', loadedBy: 'Retention Analyst', help: 'Campo restringido y no exportable. Guarda la referencia al gestor de contraseñas, nunca el secreto.' }),
      F({ key: 'accessUrl', label: 'Link de acceso', type: 'link', loadedBy: 'Retention Analyst', help: 'El sistema quita los parámetros de la lista "Parámetros de URL a eliminar" y avisa cuáles.' }),
      F({ key: 'accessOwner', label: 'Responsable del acceso', type: 'user', required: true, loadedBy: 'Lead OMC', help: 'Quien pide el alta y la baja. Si deja Winclap, se genera una alerta de revisión de acceso.' }),
      F({ key: 'expiresAt', label: 'Fecha de vencimiento', type: 'date', loadedBy: 'Retention Analyst', help: 'A menos de 30 días, la cuenta aparece marcada en el listado.' }),
      F({ key: 'accessRestriction', label: 'Restricción de acceso', type: 'text', loadedBy: 'Retention Analyst' }),
      F({ key: 'providedByClient', label: 'Provisto por el cliente', type: 'bool', required: true, default: false, loadedBy: 'Content Partner' }),
      F({ key: 'revokedAt', label: 'Baja del acceso', type: 'date', system: true, help: 'Se completa al registrar la baja del acceso.' }),
      F({ key: 'secretWarningConfirmedAt', label: 'Advertencia de secreto confirmada', type: 'date', system: true }),
    ],
  },
  journey: {
    type: 'journey', label: 'Journey', labelPlural: 'Journeys', nameField: 'name',
    actions: { duplicate: true, archive: true, delete: false, upload: true },
    fields: [
      F({ key: 'brandId', label: 'Marca / Workspace', type: 'relation', relation: 'brand', loadedBy: 'Content Partner', help: 'Requerido si la cuenta tiene más de una marca cargada.' }),
      F({ key: 'name', label: 'Nombre del journey', type: 'text', required: true, unique: true, uniqueWith: 'brandId', loadedBy: 'Content Partner' }),
      F({ key: 'objective', label: 'Objetivo', type: 'text', required: true, loadedBy: 'Content Partner' }),
      F({ key: 'targetMetric', label: 'Métrica que busca mover', type: 'text', loadedBy: 'Retention Analyst', help: 'Requerido para pasar a Activo.' }),
      F({ key: 'channels', label: 'Canales', type: 'multiselect', list: 'channel', required: true, loadedBy: 'Content Partner' }),
      F({ key: 'segmentIds', label: 'Segmentos', type: 'relations', relation: 'segment', loadedBy: 'Retention Analyst', help: 'Requerido para pasar a Activo.' }),
      F({ key: 'segmentationNotes', label: 'Descripción de la segmentación', type: 'richtext', loadedBy: 'Retention Analyst' }),
      F({ key: 'status', label: 'Estado', type: 'select', list: 'journeyStatus', required: true, default: 'Propuesto', isStatus: true, loadedBy: 'Retention Analyst', help: 'Solo el Retention Analyst o el Lead pueden pasar a Activo.' }),
      F({ key: 'activatedAt', label: 'Fecha de activación', type: 'date', loadedBy: 'Retention Analyst', help: 'Requerido si el estado es Activo.' }),
      F({ key: 'hasControlGroup', label: 'Tiene grupo control', type: 'bool', required: true, default: false, loadedBy: 'Retention Analyst' }),
      F({ key: 'nTest', label: 'Audiencia alcanzada (N test)', type: 'number', min: 0, integer: true, loadedBy: 'Retention Analyst', help: 'Personas que entraron al grupo test en el período medido. Requerido para calcular la incrementalidad.' }),
      F({ key: 'cvrTest', label: 'CVR del grupo test', type: 'percent', min: 0, max: 100, loadedBy: 'Retention Analyst', help: 'Requerido si hay grupo control y la medición es Medido. Se muestra con dos decimales.' }),
      F({ key: 'cvrControl', label: 'CVR del grupo control', type: 'percent', min: 0, max: 100, loadedBy: 'Retention Analyst' }),
      F({ key: 'uplift', label: 'Uplift vs. control', type: 'percent', system: true, help: '(CVR test − CVR control) ÷ CVR control.' }),
      F({ key: 'incrementalConversions', label: 'Conversiones incrementales', type: 'number', system: true, help: 'N test × (CVR test − CVR control), con precisión completa y truncado a entero.' }),
      F({ key: 'measurementStatus', label: 'Estado de la medición', type: 'select', list: 'measurementStatus', required: true, default: 'Sin medición', loadedBy: 'Retention Analyst' }),
      F({ key: 'measurementNote', label: 'Nota de medición', type: 'richtext', requiredIf: { field: 'measurementStatus', equals: 'Medición rota' }, loadedBy: 'Retention Analyst' }),
      F({ key: 'measuredPeriod', label: 'Período medido', type: 'text', requiredIf: { field: 'measurementStatus', equals: 'Medido' }, loadedBy: 'Retention Analyst', help: 'Sin período, los números no son comparables.' }),
      F({ key: 'designLink', label: 'Link al diseño', type: 'link', loadedBy: 'Content Partner' }),
      F({ key: 'attachments', label: 'Adjuntos', type: 'files', loadedBy: 'Content Partner', help: 'PNG, JPG o PDF. Máximo 5 MB por archivo.' }),
      F({ key: 'discardReason', label: 'Motivo del descarte', type: 'richtext', requiredIf: { field: 'status', equals: 'Descartado' }, loadedBy: 'Content Partner' }),
    ],
  },
  campaign: {
    type: 'campaign', label: 'Campaña recurrente', labelPlural: 'Campañas recurrentes', nameField: 'name',
    actions: { duplicate: true, archive: true, delete: false, upload: true },
    fields: [
      F({ key: 'brandId', label: 'Marca / Workspace', type: 'relation', relation: 'brand', loadedBy: 'Content Partner', help: 'Requerido si la cuenta tiene más de una marca cargada.' }),
      F({ key: 'name', label: 'Nombre de la campaña', type: 'text', required: true, loadedBy: 'Content Partner', help: 'Al lado se muestra la convención de naming de la cuenta (Proceso operativo "Naming de campañas"). No se valida automáticamente.' }),
      F({ key: 'campaignType', label: 'Tipo de campaña', type: 'select', list: 'campaignType', required: true, loadedBy: 'Content Partner' }),
      F({ key: 'channels', label: 'Canales', type: 'multiselect', list: 'channel', required: true, loadedBy: 'Content Partner' }),
      F({ key: 'segmentIds', label: 'Segmentos', type: 'relations', relation: 'segment', required: true, loadedBy: 'Content Partner' }),
      F({ key: 'frequency', label: 'Frecuencia', type: 'select', list: 'frequency', required: true, loadedBy: 'Content Partner' }),
      F({ key: 'sends', label: 'Envíos', type: 'number', min: 0, integer: true, loadedBy: 'Retention Analyst' }),
      F({ key: 'influencedConversions', label: 'Conversiones influenciadas', type: 'number', min: 0, integer: true, loadedBy: 'Retention Analyst' }),
      F({ key: 'cvrCommunication', label: 'CVR de la comunicación', type: 'percent', min: 0, max: 100, loadedBy: 'Retention Analyst' }),
      F({ key: 'cvrControl', label: 'CVR del control', type: 'percent', min: 0, max: 100, loadedBy: 'Retention Analyst' }),
      F({ key: 'uplift', label: 'Uplift', type: 'percent', system: true }),
      F({ key: 'incrementalConversions', label: 'Conversiones incrementales', type: 'number', system: true, help: 'Envíos × (CVR comunicación − CVR control).' }),
      F({ key: 'measuredPeriod', label: 'Período medido', type: 'text', loadedBy: 'Retention Analyst', help: 'Requerido si hay resultados cargados.' }),
      F({ key: 'bestPractices', label: 'Mejores prácticas', type: 'richtext', loadedBy: 'Content Partner' }),
      F({ key: 'tagIds', label: 'Etiquetas', type: 'relations', relation: 'tag', loadedBy: 'Content Partner' }),
      F({ key: 'attachments', label: 'Adjuntos', type: 'files', loadedBy: 'Content Partner', help: 'PNG, JPG o PDF. Máximo 5 MB por archivo. Para la pieza final.' }),
      F({ key: 'active', label: 'Activa', type: 'bool', required: true, default: true, isActiveFlag: true, loadedBy: 'Content Partner', help: 'Una campaña activa bloquea la baja de sus segmentos.' }),
    ],
  },
  experiment: {
    type: 'experiment', label: 'Experimento', labelPlural: 'Experimentos', nameField: 'name',
    actions: { duplicate: true, archive: true, delete: false, upload: true },
    fields: [
      F({ key: 'brandId', label: 'Marca / Workspace', type: 'relation', relation: 'brand', loadedBy: 'Retention Analyst' }),
      F({ key: 'journeyId', label: 'Journey asociado', type: 'relation', relation: 'journey', loadedBy: 'Retention Analyst', help: 'Un experimento se asocia a un Journey o a una Campaña, no a los dos.' }),
      F({ key: 'campaignId', label: 'Campaña asociada', type: 'relation', relation: 'campaign', loadedBy: 'Retention Analyst' }),
      F({ key: 'name', label: 'Nombre del experimento', type: 'text', required: true, loadedBy: 'Retention Analyst' }),
      F({ key: 'hypothesis', label: 'Hipótesis', type: 'richtext', required: true, loadedBy: 'Retention Analyst' }),
      F({ key: 'variable', label: 'Variable que se prueba', type: 'text', required: true, loadedBy: 'Retention Analyst', help: 'Una sola. No admite comas, punto y coma ni "y" separando dos cosas.' }),
      F({ key: 'channels', label: 'Canales', type: 'multiselect', list: 'channel', required: true, loadedBy: 'Retention Analyst', help: 'Se usa para detectar cruces con otros experimentos.' }),
      F({ key: 'segmentIds', label: 'Segmentos', type: 'relations', relation: 'segment', required: true, loadedBy: 'Retention Analyst' }),
      F({ key: 'primaryMetric', label: 'Métrica primaria', type: 'text', required: true, loadedBy: 'Retention Analyst', help: 'No puede editarse una vez que el experimento pasa a Corriendo.' }),
      F({ key: 'hasControlGroup', label: 'Tiene grupo control', type: 'bool', required: true, default: true, loadedBy: 'Retention Analyst' }),
      F({ key: 'controlGroupLevel', label: 'Nivel del grupo control', type: 'select', list: 'controlGroupLevel', requiredIf: { field: 'hasControlGroup', equals: true }, loadedBy: 'Retention Analyst', help: 'El nivel Global es el que permite reportar el valor del área al negocio.' }),
      F({ key: 'noControlReason', label: 'Por qué no hay grupo control', type: 'richtext', requiredIf: { field: 'hasControlGroup', equals: false }, loadedBy: 'Retention Analyst' }),
      F({ key: 'randomAssignment', label: 'Asignación aleatoria', type: 'bool', required: true, default: true, loadedBy: 'Retention Analyst', help: 'Si es No, el resultado se marca como no causal.' }),
      F({ key: 'flowComplexity', label: 'Clasificación del flujo', type: 'select', list: 'flowComplexity', required: true, loadedBy: 'Retention Analyst', help: 'Complejo: 30 o más pasos, 3 o más integraciones externas, o una prueba A/B interna corriendo.' }),
      F({ key: 'implementationPath', label: 'Camino de implementación', type: 'select', list: 'implementationPath', required: true, loadedBy: 'Retention Analyst', help: '"Pausar el flujo viejo" para flujos complejos; "Correr las dos versiones" para simples. Si no coincide, el sistema advierte.' }),
      F({ key: 'baseline', label: 'Baseline', type: 'percent', required: true, min: 0, max: 100, loadedBy: 'Retention Analyst' }),
      F({ key: 'mde', label: 'MDE', type: 'percent', required: true, min: 0, max: 100, loadedBy: 'Retention Analyst' }),
      F({ key: 'significanceTarget', label: 'Nivel de significancia objetivo', type: 'percent', required: true, default: 95, min: 0, max: 100, loadedBy: 'Retention Analyst' }),
      F({ key: 'powerTarget', label: 'Poder estadístico objetivo', type: 'percent', required: true, default: 80, min: 0, max: 100, loadedBy: 'Retention Analyst' }),
      F({ key: 'sampleSize', label: 'Tamaño de muestra calculado', type: 'number', required: true, min: 1, integer: true, loadedBy: 'Retention Analyst', help: 'Personas por grupo.' }),
      F({ key: 'splitTest', label: 'Split test (%)', type: 'percent', required: true, min: 1, max: 99, loadedBy: 'Retention Analyst', help: 'Entre 1 y 99. El control es 100 menos este valor.' }),
      F({ key: 'calculatorLink', label: 'Link a la calculadora usada', type: 'link', loadedBy: 'Retention Analyst' }),
      F({ key: 'startDate', label: 'Fecha de inicio', type: 'date', required: true, loadedBy: 'Retention Analyst' }),
      F({ key: 'plannedEndDate', label: 'Fecha de cierre planificada', type: 'date', required: true, loadedBy: 'Retention Analyst', help: 'No se puede adelantar una vez que el experimento está Corriendo.' }),
      F({ key: 'crossesAtypicalDate', label: 'El período cruza una fecha atípica', type: 'bool', required: true, default: false, loadedBy: 'Retention Analyst', help: 'Si es Sí, el resultado se muestra como posiblemente sesgado.' }),
      F({ key: 'status', label: 'Estado', type: 'select', list: 'experimentStatus', required: true, default: 'Diseñado', isStatus: true, loadedBy: 'Retention Analyst' }),
      F({ key: 'noImprovementPlan', label: 'Plan si no hay mejora', type: 'richtext', required: true, loadedBy: 'Retention Analyst', help: 'Se define antes de lanzar.' }),
      F({ key: 'result', label: 'Resultado', type: 'richtext', requiredIf: { field: 'status', equals: 'Cerrado' }, loadedBy: 'Retention Analyst' }),
      F({ key: 'reachedSignificance', label: '¿Alcanzó significancia?', type: 'bool', requiredIf: { field: 'status', equals: 'Cerrado' }, loadedBy: 'Retention Analyst' }),
      F({ key: 'noEffectDiagnosis', label: 'Diagnóstico si no hubo efecto', type: 'select', list: 'noUpliftDiagnosis', loadedBy: 'Retention Analyst', help: 'Requerido si está Cerrado y no alcanzó significancia.' }),
      F({ key: 'decision', label: 'Decisión', type: 'select', list: 'experimentDecision', requiredIf: { field: 'status', equals: 'Cerrado' }, loadedBy: 'Lead OMC' }),
      F({ key: 'learning', label: 'Aprendizaje', type: 'richtext', requiredIf: { field: 'status', equals: 'Cerrado' }, loadedBy: 'Lead OMC', help: 'Se completa siempre al cerrar, sin importar el resultado.' }),
      F({ key: 'attachments', label: 'Adjuntos', type: 'files', loadedBy: 'Retention Analyst', help: 'PNG, JPG o PDF. Máximo 5 MB por archivo.' }),
      F({ key: 'discardReason', label: 'Motivo del descarte', type: 'richtext', requiredIf: { field: 'status', equals: 'Descartado' }, loadedBy: 'Retention Analyst' }),
    ],
  },
  project: {
    type: 'project', label: 'Proyecto / Brief', labelPlural: 'Proyectos', nameField: 'name',
    actions: { duplicate: true, archive: false, delete: false, upload: true },
    fields: [
      F({ key: 'brandId', label: 'Marca / Workspace', type: 'relation', relation: 'brand', loadedBy: 'Content Partner' }),
      F({ key: 'name', label: 'Nombre del proyecto', type: 'text', required: true, loadedBy: 'Content Partner' }),
      F({ key: 'deliverableTypes', label: 'Tipo de entregable', type: 'multiselect', list: 'deliverableType', required: true, loadedBy: 'Content Partner' }),
      F({ key: 'pieceCount', label: 'Cantidad de piezas', type: 'number', required: true, default: 1, min: 1, integer: true, loadedBy: 'Content Partner' }),
      F({ key: 'briefReceivedAt', label: 'Fecha de bajada del brief', type: 'date', required: true, loadedBy: 'Content Partner' }),
      F({ key: 'briefComplete', label: 'La bajada está completa', type: 'bool', required: true, default: false, loadedBy: 'Content Partner', help: 'Si es No, no se puede cargar un Deadline comprometido. Se puede producir; no se puede prometer.' }),
      F({ key: 'briefMissing', label: 'Qué falta de la bajada', type: 'richtext', requiredIf: { field: 'briefComplete', equals: false }, loadedBy: 'Content Partner' }),
      F({ key: 'assetsRequestedAt', label: 'Fecha de pedido de assets', type: 'date', required: true, loadedBy: 'Content Partner', help: 'Los assets se piden el día del intake. Si es posterior a la bajada, se marca como desvío.' }),
      F({ key: 'assetsReceived', label: 'Assets recibidos', type: 'bool', required: true, default: false, loadedBy: 'Content Partner', help: 'El brief admite supuestos, los assets no. Sin assets no se pasa a En producción.' }),
      F({ key: 'assetsReceivedAt', label: 'Fecha de recepción de assets', type: 'date', requiredIf: { field: 'assetsReceived', equals: true }, loadedBy: 'Content Partner' }),
      F({ key: 'productionClockStart', label: 'Inicio del reloj de producción', type: 'date', system: true, help: 'Igual a la Fecha de recepción de assets.' }),
      F({ key: 'committedDeadline', label: 'Deadline comprometido', type: 'date', loadedBy: 'Content Partner', help: 'Solo si la bajada está completa. Si los assets se atrasan, el sistema propone correr el deadline la misma cantidad de días hábiles.' }),
      F({ key: 'status', label: 'Estado', type: 'select', list: 'projectStatus', required: true, default: 'Bajada recibida', isStatus: true, loadedBy: 'Content Partner' }),
      F({ key: 'responsible', label: 'Responsable', type: 'user', required: true, loadedBy: 'Lead OMC' }),
      F({ key: 'freelancerAssignmentId', label: 'Freelo asignado', type: 'relation', relation: 'assignment', loadedBy: 'Content Partner', help: 'Asignaciones de la cuenta con rol de diseño o maquetación.' }),
      F({ key: 'pieceLink', label: 'Link a la pieza', type: 'link', loadedBy: 'Content Partner' }),
      F({ key: 'qaDone', label: 'QA hecho', type: 'bool', required: true, default: false, loadedBy: 'Retention Analyst', help: 'No se puede cerrar un proyecto sin QA hecho.' }),
      F({ key: 'attachments', label: 'Adjuntos', type: 'files', loadedBy: 'Content Partner', help: 'PNG, JPG o PDF. Máximo 5 MB por archivo. Para la pieza entregada.' }),
    ],
  },
  process: {
    type: 'process', label: 'Proceso operativo', labelPlural: 'Procesos operativos', nameField: 'processType',
    actions: { duplicate: false, archive: true, delete: false, upload: false },
    fields: [
      F({ key: 'processType', label: 'Tipo de proceso', type: 'select', list: 'processType', required: true, unique: true, loadedBy: 'Content Partner', help: 'Único por cuenta y tipo.' }),
      F({ key: 'content', label: 'Contenido', type: 'richtext', required: true, loadedBy: 'Content Partner', help: 'Admite listas y tablas.' }),
      F({ key: 'responsible', label: 'Responsable', type: 'user', required: true, loadedBy: 'Lead OMC' }),
      F({ key: 'order', label: 'Orden', type: 'number', integer: true, min: 0, loadedBy: 'Content Partner', help: 'Para ordenar los bloques dentro de la pestaña Way of Working.' }),
      F({ key: 'lastReviewedAt', label: 'Última revisión', type: 'date', system: true }),
    ],
  },
  channelRule: {
    type: 'channelRule', label: 'Regla de canal', labelPlural: 'Reglas de canal', nameField: 'channel',
    actions: { duplicate: false, archive: true, delete: false, upload: false },
    fields: [
      F({ key: 'channel', label: 'Canal', type: 'select', list: 'channel', required: true, unique: true, loadedBy: 'Lead OMC', help: 'Único por cuenta.' }),
      F({ key: 'dailyLimitPerUser', label: 'Límite diario por usuario', type: 'number', min: 0, integer: true, loadedBy: 'Lead OMC', help: 'Vacío significa "sin límite definido", que es distinto de 0.' }),
      F({ key: 'frequentTimes', label: 'Horarios frecuentes', type: 'text', loadedBy: 'Retention Analyst' }),
      F({ key: 'timesAreHardRule', label: 'Los horarios son regla dura', type: 'bool', required: true, default: false, loadedBy: 'Lead OMC' }),
      F({ key: 'recommendedUse', label: 'Uso recomendado', type: 'richtext', required: true, loadedBy: 'Lead OMC' }),
      F({ key: 'restrictions', label: 'Restricciones', type: 'richtext', loadedBy: 'Lead OMC' }),
    ],
  },
  segment: {
    type: 'segment', label: 'Segmento', labelPlural: 'Segmentos', nameField: 'name',
    actions: { duplicate: true, archive: false, delete: false, upload: false },
    fields: [
      F({ key: 'name', label: 'Nombre del segmento', type: 'text', required: true, unique: true, loadedBy: 'Retention Analyst' }),
      F({ key: 'definition', label: 'Definición', type: 'richtext', required: true, loadedBy: 'Retention Analyst', help: 'Tiene que alcanzar para reproducir el segmento en la plataforma.' }),
      F({ key: 'segmentType', label: 'Tipo de segmento', type: 'select', list: 'segmentType', required: true, loadedBy: 'Retention Analyst' }),
      F({ key: 'approxVolume', label: 'Volumen aproximado', type: 'number', min: 0, integer: true, loadedBy: 'Retention Analyst' }),
      F({ key: 'active', label: 'Activo', type: 'bool', required: true, default: true, isActiveFlag: true, loadedBy: 'Retention Analyst', help: 'Único mecanismo de baja: no tiene archivado.' }),
    ],
  },
  tag: {
    type: 'tag', label: 'Etiqueta', labelPlural: 'Etiquetas', nameField: 'name',
    actions: { duplicate: false, archive: false, delete: false, upload: false },
    fields: [
      F({ key: 'name', label: 'Nombre de la etiqueta', type: 'text', required: true, unique: true, loadedBy: 'Content Partner' }),
      F({ key: 'purpose', label: 'Para qué se usa', type: 'text', required: true, loadedBy: 'Content Partner' }),
      F({ key: 'active', label: 'Activa', type: 'bool', required: true, default: true, isActiveFlag: true, loadedBy: 'Content Partner' }),
    ],
  },
  assignment: {
    type: 'assignment', label: 'Asignación de equipo', labelPlural: 'Asignaciones de equipo', nameField: 'displayName',
    actions: { duplicate: false, archive: false, delete: false, upload: false },
    fields: [
      F({ key: 'personType', label: 'Tipo de persona', type: 'select', list: 'personType', required: true, default: 'Winclap', loadedBy: 'Lead OMC' }),
      F({ key: 'email', label: 'Persona de Winclap', type: 'user', requiredIf: { field: 'personType', equals: 'Winclap' }, loadedBy: 'Lead OMC' }),
      F({ key: 'externalName', label: 'Persona externa', type: 'text', requiredIf: { field: 'personType', equals: 'Freelance externo' }, loadedBy: 'Lead OMC', help: 'Los freelos se registran acá pero NO reciben acceso al apartado.' }),
      F({ key: 'role', label: 'Rol', type: 'select', list: 'omRole', required: true, loadedBy: 'Lead OMC' }),
      F({ key: 'team', label: 'Equipo', type: 'select', list: 'omTeam', required: true, loadedBy: 'Lead OMC' }),
      F({ key: 'dedication', label: 'Dedicación', type: 'percent', min: 0, max: 300, restricted: 'commercial', justify: true, loadedBy: 'Lead OMC', help: 'Campo restringido. Entre 0 y 300.' }),
      F({ key: 'from', label: 'Desde', type: 'date', required: true, loadedBy: 'Lead OMC' }),
      F({ key: 'to', label: 'Hasta', type: 'date', loadedBy: 'Lead OMC', help: 'Vacío = asignación vigente.' }),
      F({ key: 'isPrimary', label: 'Es responsable principal', type: 'bool', required: true, default: false, loadedBy: 'Lead OMC', help: 'Como máximo una persona por cuenta y rol.' }),
      F({ key: 'displayName', label: 'Persona', type: 'text', system: true }),
      F({ key: 'closedReason', label: 'Motivo del cierre', type: 'text', system: true }),
    ],
  },
  link: {
    type: 'link', label: 'Link / Documento', labelPlural: 'Links y documentos', nameField: 'name',
    actions: { duplicate: false, archive: true, delete: true, upload: true },
    fields: [
      F({ key: 'name', label: 'Nombre', type: 'text', required: true, loadedBy: 'Content Partner' }),
      F({ key: 'linkType', label: 'Tipo', type: 'select', list: 'linkType', required: true, loadedBy: 'Content Partner' }),
      F({ key: 'url', label: 'URL', type: 'link', required: true, loadedBy: 'Content Partner', help: 'Se quitan los parámetros que transportan credenciales o tokens y se conservan los funcionales.' }),
      F({ key: 'resourceOwner', label: 'Dueño del recurso', type: 'select', list: 'resourceOwner', required: true, loadedBy: 'Content Partner' }),
      F({ key: 'requiresSpecialAccess', label: 'Requiere acceso especial', type: 'bool', required: true, default: false, loadedBy: 'Content Partner' }),
      F({ key: 'platformAccessId', label: 'Plataforma de acceso asociada', type: 'relation', relation: 'platform', requiredIf: { field: 'requiresSpecialAccess', equals: true }, loadedBy: 'Content Partner' }),
      F({ key: 'purpose', label: 'Función', type: 'text', loadedBy: 'Content Partner' }),
      F({ key: 'attachments', label: 'Adjuntos', type: 'files', loadedBy: 'Content Partner', help: 'PNG, JPG, SVG o PDF. Máximo 5 MB por archivo.' }),
    ],
  },
  report: {
    type: 'report', label: 'Reporte', labelPlural: 'Reportes', nameField: 'name',
    actions: { duplicate: false, archive: true, delete: false, upload: false },
    fields: [
      F({ key: 'name', label: 'Nombre del reporte', type: 'text', required: true, loadedBy: 'Content Partner' }),
      F({ key: 'frequency', label: 'Frecuencia', type: 'select', list: 'frequency', required: true, loadedBy: 'Content Partner' }),
      F({ key: 'format', label: 'Formato', type: 'select', list: 'reportFormat', required: true, loadedBy: 'Content Partner' }),
      F({ key: 'link', label: 'Link', type: 'link', required: true, loadedBy: 'Content Partner' }),
      F({ key: 'clientFocus', label: 'Qué mira el cliente', type: 'richtext', required: true, loadedBy: 'Growth Partner' }),
      F({ key: 'analysisHistory', label: 'Histórico de análisis', type: 'richtext', loadedBy: 'Retention Analyst', help: 'Un renglón por período con su foco y su aprendizaje.' }),
      F({ key: 'responsible', label: 'Responsable', type: 'user', required: true, loadedBy: 'Lead OMC' }),
    ],
  },
  commChannel: {
    type: 'commChannel', label: 'Canal de comunicación con el cliente', labelPlural: 'Canales de comunicación con el cliente', nameField: 'name',
    actions: { duplicate: false, archive: true, delete: false, upload: false },
    fields: [
      F({ key: 'channelType', label: 'Tipo de canal', type: 'select', list: 'commChannelType', required: true, loadedBy: 'Content Partner' }),
      F({ key: 'name', label: 'Nombre concreto', type: 'text', required: true, loadedBy: 'Content Partner', help: 'Por ejemplo "grupo Shell & Winclap" o "#shell-chile-om-content".' }),
      F({ key: 'intensity', label: 'Intensidad de uso', type: 'select', list: 'usageIntensity', required: true, loadedBy: 'Content Partner' }),
    ],
  },
};

export function fieldDef(type: OmlEntityType, key: string): OmlFieldDef | undefined {
  return OML_SCHEMA[type].fields.find((f) => f.key === key);
}

/** Campos restringidos por entidad (§9.3): la lista es CERRADA y sale del schema. */
export const OML_RESTRICTED_FIELDS: Partial<Record<OmlEntityType, { key: string; group: OmlRestrictedGroup }[]>> = Object.fromEntries(
  OML_ENTITY_TYPES.map((t) => [t, OML_SCHEMA[t].fields.filter((f) => f.restricted).map((f) => ({ key: f.key, group: f.restricted! }))]).filter(([, v]) => (v as unknown[]).length),
);

/** Campos que exigen justificación al cambiar (§8.5 / §13.3). */
export const OML_JUSTIFY_FIELDS: Partial<Record<OmlEntityType, string[]>> = Object.fromEntries(
  OML_ENTITY_TYPES.map((t) => [t, OML_SCHEMA[t].fields.filter((f) => f.justify).map((f) => f.key)]).filter(([, v]) => (v as unknown[]).length),
);

// ─── Máquinas de estado (§13.2) ──────────────────────────────────────────────

/** Rol OML a efectos de "quién lo activa". `lead` y `admin` siempre pueden. */
export type OmlActorRole = 'admin' | 'lead' | 'direction' | 'gp' | 'cp' | 'ra' | 'nominal' | 'system';

export interface OmlTransition {
  to: string;
  /** Roles que pueden disparar el cambio (además de lead/admin). Vacío = solo lead/admin. */
  by: OmlActorRole[];
}

export const OML_TRANSITIONS: Partial<Record<OmlEntityType, Record<string, OmlTransition[]>>> = {
  account: {
    [ACCOUNT_STATUS.ONBOARDING]: [{ to: ACCOUNT_STATUS.ACTIVE, by: [] }, { to: ACCOUNT_STATUS.ARCHIVED, by: [] }],
    [ACCOUNT_STATUS.ACTIVE]: [{ to: ACCOUNT_STATUS.PAUSED, by: [] }, { to: ACCOUNT_STATUS.CLOSED, by: [] }],
    [ACCOUNT_STATUS.PAUSED]: [{ to: ACCOUNT_STATUS.ACTIVE, by: [] }, { to: ACCOUNT_STATUS.CLOSED, by: [] }],
    [ACCOUNT_STATUS.CLOSED]: [{ to: ACCOUNT_STATUS.ARCHIVED, by: [] }, { to: ACCOUNT_STATUS.ACTIVE, by: [] }],
    [ACCOUNT_STATUS.ARCHIVED]: [{ to: ACCOUNT_STATUS.CLOSED, by: [] }],
  },
  journey: {
    [JOURNEY_STATUS.PROPOSED]: [{ to: JOURNEY_STATUS.DESIGN, by: ['cp'] }, { to: JOURNEY_STATUS.DISCARDED, by: ['cp', 'ra'] }],
    [JOURNEY_STATUS.DESIGN]: [{ to: JOURNEY_STATUS.IMPLEMENTATION, by: ['ra'] }, { to: JOURNEY_STATUS.PROPOSED, by: ['cp', 'ra'] }, { to: JOURNEY_STATUS.DISCARDED, by: ['cp'] }],
    [JOURNEY_STATUS.IMPLEMENTATION]: [{ to: JOURNEY_STATUS.ACTIVE, by: ['ra'] }, { to: JOURNEY_STATUS.DESIGN, by: ['cp', 'ra'] }, { to: JOURNEY_STATUS.DISCARDED, by: ['cp'] }],
    [JOURNEY_STATUS.ACTIVE]: [{ to: JOURNEY_STATUS.PAUSED, by: ['ra'] }],
    [JOURNEY_STATUS.PAUSED]: [{ to: JOURNEY_STATUS.ACTIVE, by: ['ra'] }, { to: JOURNEY_STATUS.ARCHIVED, by: [] }],
    [JOURNEY_STATUS.DISCARDED]: [{ to: JOURNEY_STATUS.ARCHIVED, by: [] }],
    [JOURNEY_STATUS.ARCHIVED]: [{ to: JOURNEY_STATUS.PAUSED, by: [] }],
  },
  experiment: {
    [EXPERIMENT_STATUS.DESIGNED]: [{ to: EXPERIMENT_STATUS.RUNNING, by: ['ra'] }, { to: EXPERIMENT_STATUS.DISCARDED, by: ['ra'] }],
    [EXPERIMENT_STATUS.RUNNING]: [{ to: EXPERIMENT_STATUS.CLOSED, by: ['ra'] }],
    [EXPERIMENT_STATUS.CLOSED]: [],
    [EXPERIMENT_STATUS.DISCARDED]: [],
  },
  project: {
    [PROJECT_STATUS.BRIEF]: [{ to: PROJECT_STATUS.AWAITING_ASSETS, by: ['cp', 'system'] }, { to: PROJECT_STATUS.PRODUCTION, by: ['cp'] }, { to: PROJECT_STATUS.CANCELLED, by: ['cp'] }],
    [PROJECT_STATUS.AWAITING_ASSETS]: [{ to: PROJECT_STATUS.PRODUCTION, by: ['cp'] }, { to: PROJECT_STATUS.CANCELLED, by: ['cp'] }],
    [PROJECT_STATUS.PRODUCTION]: [{ to: PROJECT_STATUS.INTERNAL_REVIEW, by: ['cp'] }, { to: PROJECT_STATUS.CANCELLED, by: ['cp'] }],
    [PROJECT_STATUS.INTERNAL_REVIEW]: [{ to: PROJECT_STATUS.CLIENT_VALIDATION, by: ['cp'] }, { to: PROJECT_STATUS.PRODUCTION, by: ['cp'] }],
    [PROJECT_STATUS.CLIENT_VALIDATION]: [{ to: PROJECT_STATUS.APPROVED, by: ['cp'] }, { to: PROJECT_STATUS.PRODUCTION, by: ['cp'] }],
    [PROJECT_STATUS.APPROVED]: [{ to: PROJECT_STATUS.DELIVERED, by: ['ra', 'cp'] }],
    [PROJECT_STATUS.DELIVERED]: [],
    [PROJECT_STATUS.CANCELLED]: [],
  },
};

// ─── Permisos (§8.3 + §9.2) ──────────────────────────────────────────────────

export type OmlAction = 'create' | 'edit' | 'duplicate' | 'archive' | 'delete' | 'upload';

/**
 * Tabla 8.3: qué rol de asignación puede hacer qué sobre cada entidad. Lead OMC
 * y Admin del apartado pueden todo salvo lo que la tabla reserva ("Solo el Lead
 * OMC" ya los incluye). El GP solo edita los campos comerciales de la cuenta.
 */
export interface OmlEntityPermission {
  create: OmlActorRole[];
  edit: OmlActorRole[];
  duplicate: OmlActorRole[];
  archive: OmlActorRole[];
  delete: OmlActorRole[];
  upload: OmlActorRole[];
}
const TEAM: OmlActorRole[] = ['cp', 'ra', 'gp'];
const CP_RA: OmlActorRole[] = ['cp', 'ra'];
const NONE: OmlActorRole[] = [];
export const OML_PERMISSIONS: Record<OmlEntityType, OmlEntityPermission> = {
  account: { create: NONE, edit: TEAM, duplicate: NONE, archive: NONE, delete: NONE, upload: TEAM },
  brand: { create: CP_RA, edit: CP_RA, duplicate: NONE, archive: CP_RA, delete: NONE, upload: NONE },
  stakeholder: { create: CP_RA, edit: CP_RA, duplicate: NONE, archive: NONE, delete: NONE, upload: NONE },
  platform: { create: ['ra'], edit: ['ra'], duplicate: NONE, archive: ['ra'], delete: NONE, upload: NONE },
  journey: { create: TEAM, edit: TEAM, duplicate: TEAM, archive: TEAM, delete: NONE, upload: TEAM },
  campaign: { create: CP_RA, edit: CP_RA, duplicate: CP_RA, archive: CP_RA, delete: NONE, upload: CP_RA },
  experiment: { create: ['ra'], edit: ['ra'], duplicate: ['ra'], archive: ['ra'], delete: NONE, upload: ['ra'] },
  project: { create: ['cp'], edit: ['cp'], duplicate: ['cp'], archive: NONE, delete: NONE, upload: ['cp'] },
  process: { create: CP_RA, edit: CP_RA, duplicate: NONE, archive: CP_RA, delete: NONE, upload: NONE },
  channelRule: { create: NONE, edit: NONE, duplicate: NONE, archive: NONE, delete: NONE, upload: NONE },
  segment: { create: ['ra'], edit: ['ra'], duplicate: ['ra'], archive: NONE, delete: NONE, upload: NONE },
  tag: { create: ['cp'], edit: ['cp'], duplicate: NONE, archive: NONE, delete: NONE, upload: NONE },
  assignment: { create: NONE, edit: NONE, duplicate: NONE, archive: NONE, delete: NONE, upload: NONE },
  link: { create: TEAM, edit: TEAM, duplicate: NONE, archive: TEAM, delete: NONE, upload: TEAM },
  report: { create: ['cp'], edit: ['cp'], duplicate: NONE, archive: ['cp'], delete: NONE, upload: NONE },
  commChannel: { create: CP_RA, edit: CP_RA, duplicate: NONE, archive: CP_RA, delete: NONE, upload: NONE },
};

/** Campos de la cuenta que el Growth Partner SÍ edita (§9.2: "Solo el Deal, la vigencia y los topes"). */
export const GP_EDITABLE_ACCOUNT_FIELDS: readonly string[] = ['deal', 'dealValidUntil', 'contractCaps', 'sowLink'];

/** Matriz 9.2 por rol (lo general; 8.3 acota por entidad). Editable con overrides desde el panel. */
export interface OmlRoleMatrixRow {
  view: 'all' | 'own' | 'granted' | 'none';
  create: boolean;
  editOwn: boolean;
  editAll: boolean;
  archive: boolean;
  export: 'all' | 'own' | 'granted' | 'none';
  history: 'all' | 'own' | 'none';
  admin: boolean;
}
export const OML_DEFAULT_ROLE_MATRIX: Record<Exclude<OmlActorRole, 'system'> | 'rest', OmlRoleMatrixRow> = {
  direction: { view: 'all', create: false, editOwn: false, editAll: false, archive: false, export: 'all', history: 'all', admin: false },
  // GP: §9.2 decía crear/archivar "no", pero la tabla 8.3 le da journey y link (TEAM) y "manda 8.3 cuando no
  // coinciden": desde que la matriz se aplica (16/10/2026) el default refleja lo que de verdad puede.
  gp: { view: 'own', create: true, editOwn: true, editAll: false, archive: true, export: 'own', history: 'all', admin: false },
  lead: { view: 'all', create: true, editOwn: true, editAll: true, archive: true, export: 'all', history: 'all', admin: true },
  cp: { view: 'own', create: true, editOwn: true, editAll: false, archive: true, export: 'none', history: 'own', admin: false },
  ra: { view: 'own', create: true, editOwn: true, editAll: false, archive: true, export: 'none', history: 'own', admin: false },
  admin: { view: 'all', create: true, editOwn: true, editAll: true, archive: true, export: 'all', history: 'all', admin: true },
  rest: { view: 'none', create: false, editOwn: false, editAll: false, archive: false, export: 'none', history: 'none', admin: false },
  // Nominal: historial "propio" = la cuenta del grant (así lo veía antes de que la matriz se aplicara; §9.2 decía "nada").
  nominal: { view: 'granted', create: false, editOwn: false, editAll: false, archive: false, export: 'granted', history: 'own', admin: false },
};

// ─── Governance / settings (ConfigTable) ─────────────────────────────────────

export interface OmlGovernance {
  /** Admins del apartado (lista manual). Además, los admins de plataforma. */
  admins: string[];
  /** Lead OMC / Retention Lead (lista manual). */
  leads: string[];
  /** Dirección: emails fijos + niveles de la Nómina que cuentan como Dirección. */
  directionEmails: string[];
  directionLevels: string[];
  /** Data owner del apartado (aprueba solicitudes). Nunca puede quedar vacío una vez seteado. */
  dataOwner: string | null;
  /** Director de Owned Media: segunda firma para solicitudes de personas ajenas al área. */
  directorOm: string | null;
  /** Áreas/teams del community map que se consideran "propias" (Owned Media, Retention, Growth, Dirección). */
  ownAreas: string[];
  /** Overrides de la matriz 9.2 (solo el Lead OMC/admin los cambian; quedan en auditoría). */
  roleMatrix?: Partial<Record<keyof typeof OML_DEFAULT_ROLE_MATRIX, Partial<OmlRoleMatrixRow>>>;
}

export const OML_DEFAULT_GOVERNANCE: OmlGovernance = {
  admins: [],
  leads: [],
  directionEmails: [],
  directionLevels: ['C-Level', 'VP'],
  dataOwner: null,
  directorOm: null,
  ownAreas: ['Owned Media', 'Retention', 'Growth', 'Dirección'],
};

export interface OmlAccountTemplate {
  id: string;
  serviceType: string;
  name: string;
  /** Bloques de proceso operativo precargados. */
  processes: { processType: string; content: string }[];
  /** Reglas de canal precargadas. */
  channelRules: { channel: string; dailyLimitPerUser?: number | null; recommendedUse: string; restrictions?: string }[];
  /** Campos típicos de la cuenta. */
  accountDefaults: Record<string, unknown>;
}

export interface OmlSettings {
  staleDays: number; // 30
  staleEscalateDays: number; // 45
  accessRequestEscalateBusinessDays: number; // 5
  projectAwaitingAssetsBusinessDays: number; // 3
  accessExpiryWarnDays: number[]; // [30, 15, 7]
  currentStatusStaleDays: number; // 30
  draftTtlDays: number; // 7
  retentionMonthsAfterClose: number; // 24
  auditRetentionMonths: number; // 24
  /** Textos, títulos y descripciones editables del apartado (§11.2). */
  texts: Record<string, string>;
  templates: OmlAccountTemplate[];
  /** Kill-switch de notificaciones por Slack (in-app siempre). */
  slackNotifications: boolean;
}

export const OML_DEFAULT_SETTINGS: OmlSettings = {
  staleDays: 30,
  staleEscalateDays: 45,
  accessRequestEscalateBusinessDays: 5,
  projectAwaitingAssetsBusinessDays: 3,
  accessExpiryWarnDays: [30, 15, 7],
  currentStatusStaleDays: 30,
  draftTtlDays: 7,
  retentionMonthsAfterClose: 24,
  auditRetentionMonths: 24,
  texts: {},
  templates: [],
  slackNotifications: true,
};

// ─── Registros ───────────────────────────────────────────────────────────────

export interface OmlEntityMeta {
  id: string;
  accountId: string;
  type: OmlEntityType;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  /** Versión por campo: `fieldVersions[key] = updatedAt` del último cambio (conflictos por campo, §13.4). */
  fieldVersions: Record<string, string>;
  version: number;
  archived: boolean;
  archivedAt?: string;
  archivedBy?: string;
}

export type OmlEntity = OmlEntityMeta & Record<string, unknown>;

export interface OmlAccount extends OmlEntityMeta {
  type: 'account';
  name: string;
  client: string;
  countries: string[];
  serviceTypes: string[];
  startDate: string;
  endDate?: string | null;
  previousPeriods?: string;
  status: string;
  functionalOwner: string;
  clientSummary: string;
  currentStatus: string;
  mainObjective: string;
  kpis?: string;
  attributionWindow?: string;
  metricsProvider: string;
  deal?: string;
  dealValidUntil?: string | null;
  contractCaps?: string;
  sowLink?: string;
  capacitySummary?: string;
  activeChannels?: string[];
  logoAssetId?: string | null;
  retentionDeadline?: string | null;
  lastReviewedAt: string;
  currentStatusUpdatedAt?: string;
  closedAt?: string | null;
  [k: string]: unknown;
}

export interface OmlAssignment extends OmlEntityMeta {
  type: 'assignment';
  personType: string;
  email?: string | null;
  externalName?: string | null;
  role: string;
  team: string;
  dedication?: number | null;
  from: string;
  to?: string | null;
  isPrimary: boolean;
  displayName?: string;
  closedReason?: string;
  [k: string]: unknown;
}

/** Fila del historial (append-only). Para restringidos, `from`/`to` solo viajan a quien puede verlos. */
export interface OmlHistoryRow {
  id: string;
  accountId: string;
  entityType: OmlEntityType;
  entityId: string;
  entityName: string;
  field: string;
  from: unknown;
  to: unknown;
  restricted: boolean;
  /** Justificación escrita, cuando el cambio la exige. */
  justification?: string;
  /** Confirmación explícita de "no es una clave" en Dónde está la credencial. */
  secretWarningConfirmed?: boolean;
  by: string;
  byRole: OmlActorRole;
  at: string;
  /** 'create' | 'update' | 'archive' | 'restore' | 'status' | 'delete' */
  kind: 'create' | 'update' | 'archive' | 'restore' | 'status' | 'delete';
}

export type OmlAuditEvent =
  | 'view_account'
  | 'view_restricted'
  | 'export'
  | 'export_pdf'
  | 'permission_change'
  | 'catalog_change'
  | 'settings_change'
  | 'access_request_created'
  | 'access_request_approved'
  | 'access_request_rejected'
  | 'access_request_escalated'
  | 'access_grant_expired'
  | 'platform_access_created'
  | 'platform_access_revoked'
  | 'platform_access_renewed'
  | 'access_denied'
  | 'account_created'
  | 'account_archived'
  | 'account_restored'
  | 'account_closed'
  | 'assignment_closed_by_sync'
  | 'delete_link'
  | 'physical_delete';

export interface OmlAuditRow {
  id: string;
  at: string;
  by: string;
  byRole: OmlActorRole | 'rest';
  event: OmlAuditEvent;
  accountId?: string;
  accountName?: string;
  entityType?: OmlEntityType;
  entityId?: string;
  field?: string;
  detail?: Record<string, unknown>;
  ip?: string;
}

export type OmlAccessScope = 'commercial' | 'export';

export interface OmlAccessRequest {
  id: string;
  requester: string;
  requesterArea?: string;
  /** Persona ajena a las áreas propias ⇒ exige doble firma (§10.2). */
  external: boolean;
  scope: OmlAccessScope;
  accountId: string | null; // null = todas (solo exportación)
  accountName?: string;
  field?: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  createdAt: string;
  escalatedAt?: string | null;
  /** Firmas: data owner (siempre) + director OM (si external). */
  approvals: { by: string; role: 'data_owner' | 'director_om' | 'lead'; at: string }[];
  resolvedAt?: string | null;
  resolvedBy?: string | null;
  rejectReason?: string;
  expiresAt?: string | null; // fecha de vencimiento del acceso otorgado
  /** Optimistic locking: dos firmas concurrentes no se pisan (code-reviewer 23/09). */
  version: number;
}

export interface OmlGrant {
  email: string;
  scope: OmlAccessScope;
  accountId: string | null;
  field?: string;
  grantedBy: string;
  grantedAt: string;
  expiresAt: string;
  requestId: string;
}

export interface OmlSavedView {
  id: string;
  name: string;
  filters: Record<string, unknown>;
  createdAt: string;
}

export interface OmlDraft {
  entityKey: string; // `${type}#${accountId}#${id|new}`
  values: Record<string, unknown>;
  savedAt: string;
}

export interface OmlAsset {
  id: string;
  accountId: string;
  name: string;
  contentType: string;
  size: number;
  uploadedBy: string;
  uploadedAt: string;
  key: string;
}

export const OML_MAX_FILE_BYTES = 5 * 1024 * 1024;
export const OML_ALLOWED_MIME: readonly string[] = ['image/png', 'image/jpeg', 'image/svg+xml', 'application/pdf'];

/** Cómo se ve la ficha para un usuario: qué campos restringidos puede ver y qué puede hacer. */
export interface OmlAccountAccess {
  accountId: string;
  role: OmlActorRole | 'rest';
  roles: OmlActorRole[];
  canView: boolean;
  canEditAccount: boolean;
  canSeeCommercial: boolean;
  canSeeCredential: boolean;
  canExport: boolean;
  canArchive: boolean;
  canSeeArchived: boolean;
  /** Historial de cambios de la cuenta (columna "historial" de la matriz 9.2: todo / propio / nada). */
  canSeeHistory: boolean;
  /** Acciones por entidad ya resueltas (8.3 ∩ 9.2). */
  entities: Record<OmlEntityType, Partial<Record<OmlAction, boolean>>>;
  /** Solo la parte editable del GP. */
  editableAccountFields: 'all' | readonly string[] | 'none';
  isTeam: boolean;
}

/** Estado de la ficha, derivado (§6.2). */
export type OmlFichaStatus = 'al_dia' | 'desactualizada' | 'no_aplica';

export interface OmlCompleteness {
  percent: number;
  blocks: { key: OmlCompletenessBlock; ok: boolean }[];
  /** Campos recomendados que faltan (KPIs, ventana de atribución, capacity, deal). */
  missingRecommended: string[];
  /** Campos requeridos del schema que faltan (sin pisar el guardado: se listan en Salud). */
  missingRequired: string[];
}
export type OmlCompletenessBlock = 'general' | 'currentStatus' | 'stakeholder' | 'platform' | 'assignment' | 'channelRule' | 'calendarLink';
export const OML_COMPLETENESS_BLOCKS: readonly OmlCompletenessBlock[] = ['general', 'currentStatus', 'stakeholder', 'platform', 'assignment', 'channelRule', 'calendarLink'];
